import { useEffect, useRef } from 'react';
import { FundCriteria } from '../../types/fundScreener.type';
import { getFundScreenerMinMax } from '../../api/fundScreener.api';
import { toApiPeriod } from '../../constants/fundScreener/fundCriteria.constant';

interface UseFundCriterionMinMaxParams {
  market: string;
  token: string;
  criterion: FundCriteria;
  tempCriteria: FundCriteria[];
  // `marketChanged` tells the caller this fetch is for a different market
  // than the one its current selection was fetched for — same idea as the
  // period check in FundCriteriaSetting's onResult, so a stale selection
  // isn't preserved onto the new market's scale.
  onResult: (range: { min: number; max: number; marketChanged: boolean }) => void;
  onPendingChange: (pending: boolean) => void;
  // Called when the BE has no range for this criterion (null min/max, or the
  // request itself fails) — there's nothing meaningful to filter by yet.
  onUnavailable: () => void;
}

// assetType/classification are NOT sent to /minmax; CURRENCY and
// mgmtCompanyName are, sourced from whichever criteria are selected.
const getFilterValues = (tempCriteria: FundCriteria[]) => ({
  CURRENCY: tempCriteria.find((item) => item.id === 'fundCurrency' && item.selected)
    ?.currency,
  mgmtCompanyName: tempCriteria.find((item) => item.id === 'fundCompany' && item.selected)
    ?.companyName,
});

// Fetches min/max for a range criterion when selected, its period changes,
// or the market changes. Skips criteria without `apiField` or with
// `supportsMinMax: false`.
//
// Re-mounts on every popup open/close, so the first effect run on a fresh
// mount can't tell "freshly selected" apart from "already fetched, just
// reopening the popup" using local state alone — `criterion.minMaxMarket`
// (persisted on the criteria array, not local refs) is what makes that
// distinction survive the unmount/remount.
export const useFundCriterionMinMax = ({
  market,
  token,
  criterion,
  tempCriteria,
  onResult,
  onPendingChange,
  onUnavailable,
}: UseFundCriterionMinMaxParams) => {
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (
      criterion.controlType !== 'range' ||
      criterion.supportsMinMax === false ||
      !criterion.apiField
    ) {
      return;
    }

    const wasFirstRun = isFirstRun.current;
    isFirstRun.current = false;

    // Reopening the popup with nothing actually changed — keep showing the
    // already-fetched range instead of refetching.
    if (wasFirstRun && criterion.selectedMin !== undefined && criterion.minMaxMarket === market) {
      return;
    }

    onPendingChange(true);
    const marketChanged = criterion.minMaxMarket !== undefined && criterion.minMaxMarket !== market;
    const { CURRENCY, mgmtCompanyName } = getFilterValues(tempCriteria);

    getFundScreenerMinMax(
      {
        market,
        field: criterion.apiField,
        period: toApiPeriod(criterion.timeValue),
        CURRENCY,
        mgmtCompanyName,
      },
      token,
    )
      .then((range) => {
        if (range.min === null || range.min === undefined || range.max === null || range.max === undefined) {
          onUnavailable();
        } else {
          onResult({ min: range.min, max: range.max, marketChanged });
        }
        onPendingChange(false);
      })
      .catch(() => {
        onUnavailable();
        onPendingChange(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criterion.id, criterion.timeValue, market, token]);
};